import React, { createContext, FC, HTMLAttributes, KeyboardEvent, ReactElement, ReactNode, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../icon';

type TabContextValue = {
    onSelect?: (index: number, lastIndex: number, event: React.MouseEvent | KeyboardEvent) => boolean | void;
    selectedIndex: number;
    setSelectedIndex: (index: number) => void;
};

const TabContext = createContext<TabContextValue>({
    selectedIndex: 0,
    setSelectedIndex: () => {}
});

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
    defaultIndex?: number;
    forceRenderTabPanel?: boolean;
    onSelect?: (index: number, lastIndex: number, event: React.MouseEvent | KeyboardEvent) => boolean | void;
    selectedIndex?: number;
}

type InternalTabProps = {
    __ztabIndex?: number;
};

type InternalTabPanelProps = {
    __ztabForceRender?: boolean;
    __ztabPanelIndex?: number;
};

export interface TabProps extends Omit<HTMLAttributes<HTMLLIElement>, 'tabIndex'> {
    disabled?: boolean;
    tabIndex?: number | string;
}

export interface TabListProps extends HTMLAttributes<HTMLUListElement> {
    hasOverflow?: boolean;
}

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
    forceRender?: boolean;
}

const TabRoot = styled.li<{ $disabled?: boolean; $selected?: boolean }>`
    white-space: nowrap;
    outline: none;
    font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
    display: inline-flex;
    align-items: center;
    gap: 6px;
    list-style: none;
    height: 28px;
    padding: 0 14px;
    box-sizing: border-box;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    font-size: 13px;
    font-family: inherit;
    color: ${({ $disabled, $selected, theme }) => {
        if ($disabled) return theme.app.primary_light;
        if ($selected) return theme.app.textColor;
        return theme.app.primary;
    }};
    background: ${({ $selected, theme }) => ($selected ? theme.app.backgroundColor : 'transparent')};
    border: none;
    border-radius: 6px;
    box-shadow: ${({ $selected }) => ($selected ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none')};
    transition: all 0.15s;

    &:hover {
        color: ${({ $disabled, theme }) => ($disabled ? theme.app.primary_light : theme.app.textColor)};
    }

    ${Icon} {
        width: 16px;
        height: 16px;
    }
`;

const TabListRoot = styled.ul<{ $hasOverflow?: boolean }>`
    position: relative;
    margin: 0 0 16px;
    padding: 2px;
    width: fit-content;
    overflow: ${({ $hasOverflow }) => ($hasOverflow ? 'auto hidden' : 'visible')};
    display: inline-flex;
    min-width: 0;
    flex-wrap: nowrap;
    gap: 2px;
    background: ${(props) => props.theme.app.pageBackgroundColor};
    border-radius: 8px;
    border: none;
    scrollbar-width: ${({ $hasOverflow }) => ($hasOverflow ? 'none' : 'auto')};

    &::-webkit-scrollbar {
        display: ${({ $hasOverflow }) => ($hasOverflow ? 'none' : 'initial')};
    }
`;

const TabPanelRoot = styled.div<{ $selected?: boolean }>`
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};

    &[hidden] {
        display: none !important;
    }
`;

export const Tab: FC<TabProps & InternalTabProps> = ({ __ztabIndex = 0, children, className, disabled, onClick, onKeyDown, tabIndex, ...rest }) => {
    const { onSelect, selectedIndex, setSelectedIndex } = useContext(TabContext);
    const isSelected = selectedIndex === __ztabIndex;

    const selectTab = (event: React.MouseEvent<HTMLLIElement> | KeyboardEvent<HTMLLIElement>) => {
        if (disabled) return;

        const shouldSelect = onSelect?.(__ztabIndex, selectedIndex, event);
        if (shouldSelect === false) return;

        setSelectedIndex(__ztabIndex);
    };

    const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
        onClick?.(event);
        if (!event.defaultPrevented) selectTab(event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || (event.key !== 'Enter' && event.key !== ' ')) return;

        event.preventDefault();
        selectTab(event);
    };

    const resolvedClassName = ['react-tabs__tab', isSelected ? 'react-tabs__tab--selected' : undefined, className].filter(Boolean).join(' ');

    return (
        <TabRoot
            {...rest}
            $disabled={disabled}
            $selected={isSelected}
            aria-disabled={disabled || undefined}
            aria-selected={isSelected}
            className={resolvedClassName}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role='tab'
            tabIndex={disabled ? undefined : Number(tabIndex ?? 0)}
        >
            {children}
        </TabRoot>
    );
};

export const TabList: FC<TabListProps> = ({ children, className, hasOverflow, ...rest }) => (
    <TabListRoot {...rest} $hasOverflow={hasOverflow} className={['react-tabs__tab-list', className].filter(Boolean).join(' ')} role='tablist'>
        {children}
    </TabListRoot>
);

export const TabPanel: FC<TabPanelProps & InternalTabPanelProps> = ({ __ztabForceRender, __ztabPanelIndex = 0, children, className, forceRender, ...rest }) => {
    const { selectedIndex } = useContext(TabContext);
    const isSelected = selectedIndex === __ztabPanelIndex;
    const shouldRenderChildren = isSelected || forceRender || __ztabForceRender;
    const resolvedClassName = ['react-tabs__tab-panel', isSelected ? 'react-tabs__tab-panel--selected' : undefined, className].filter(Boolean).join(' ');

    return (
        <TabPanelRoot {...rest} $selected={isSelected} className={resolvedClassName} role='tabpanel' hidden={!isSelected}>
            {shouldRenderChildren ? children : null}
        </TabPanelRoot>
    );
};

const getBaseComponentType = (component: unknown): unknown => {
    const styledComponent = component as { target?: unknown } | undefined;
    if (!styledComponent?.target) return component;

    return getBaseComponentType(styledComponent.target);
};

const isElementOfType = <P,>(node: ReactNode, component: FC<P>): node is ReactElement<P> =>
    React.isValidElement(node) && getBaseComponentType(node.type) === component;

const enhanceTabsChildren = (children: ReactNode, forceRenderTabPanel?: boolean) => {
    let tabIndex = 0;
    let panelIndex = 0;

    const enhanceNode = (node: ReactNode): ReactNode => {
        if (!React.isValidElement(node)) return node;

        if (isElementOfType(node, Tab)) {
            return React.cloneElement(node, { __ztabIndex: tabIndex++ });
        }

        if (isElementOfType(node, TabPanel)) {
            return React.cloneElement(node, { __ztabForceRender: forceRenderTabPanel, __ztabPanelIndex: panelIndex++ });
        }

        const nodeElement = node as ReactElement<{ children?: ReactNode }>;
        const nodeProps = nodeElement.props;
        if (nodeProps.children == null) return node;

        return React.cloneElement(nodeElement, {
            children: React.Children.map(nodeProps.children, enhanceNode)
        });
    };

    return React.Children.map(children, enhanceNode);
};

const TabsRoot: FC<TabsProps> = ({ children, className, defaultIndex = 0, forceRenderTabPanel, onSelect, selectedIndex, ...rest }) => {
    const [internalSelectedIndex, setInternalSelectedIndex] = useState(defaultIndex);
    const resolvedSelectedIndex = selectedIndex ?? internalSelectedIndex;
    const enhancedChildren = useMemo(() => enhanceTabsChildren(children, forceRenderTabPanel), [children, forceRenderTabPanel]);

    const contextValue = useMemo<TabContextValue>(
        () => ({
            onSelect,
            selectedIndex: resolvedSelectedIndex,
            setSelectedIndex: selectedIndex == null ? setInternalSelectedIndex : () => {}
        }),
        [onSelect, resolvedSelectedIndex, selectedIndex]
    );

    return (
        <TabContext.Provider value={contextValue}>
            <div {...rest} className={className}>
                {enhancedChildren}
            </div>
        </TabContext.Provider>
    );
};

export const ZTabs = styled(TabsRoot)`
    -webkit-tap-highlight-color: transparent;
`;
